import { useEffect, useId, useState } from "react";
import { Check, Mic } from "lucide-react";

const transitions = {
  recording: { next: "request", delay: 1200 },
  request: { next: "care", delay: 900 },
  care: { next: "confirmed", delay: 1100 },
  confirmed: { next: "complete", delay: 800 },
};

export default function LandingCareDemo() {
  const [phase, setPhase] = useState("idle");
  const [reducedMotion, setReducedMotion] = useState(() =>
    typeof window !== "undefined" && Boolean(window.matchMedia?.("(prefers-reduced-motion: reduce)").matches)
  );
  const hintId = useId();
  const playing = Boolean(transitions[phase]);
  const idle = phase === "idle";
  const showRequest = ["request", "care", "confirmed", "complete"].includes(phase);
  const showCare = ["care", "confirmed", "complete"].includes(phase);
  const showAppointment = ["confirmed", "complete"].includes(phase);

  useEffect(() => {
    const preference = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    if (!preference) return undefined;
    const update = () => setReducedMotion(preference.matches);
    update();
    preference.addEventListener?.("change", update);
    return () => preference.removeEventListener?.("change", update);
  }, []);

  useEffect(() => {
    const transition = transitions[phase];
    if (!transition) return undefined;
    if (reducedMotion) {
      setPhase("complete");
      return undefined;
    }
    // Only one phase timer exists; phase changes and unmount cancel it.
    const timer = window.setTimeout(() => setPhase(transition.next), transition.delay);
    return () => window.clearTimeout(timer);
  }, [phase, reducedMotion]);

  const start = () => {
    if (playing) return;
    setPhase(reducedMotion ? "complete" : "recording");
  };

  return (
    <div className="pc-care-demo" data-care-phase={phase}>
      <div className="pc-care-demo__story">
        {phase === "recording" && (
          <div className="pc-care-demo__waveform" aria-hidden="true">
            {[12, 22, 34, 20, 42, 26, 34, 18, 12].map((height, index) =>
              <span key={index} style={{ height, animationDelay: `${index * -0.11}s` }} />
            )}
          </div>
        )}
        {showRequest && <blockquote className="pc-care-demo__request">Kannst du mir morgen einen Physiotermin organisieren?</blockquote>}
        {showCare && (
          <div className="pc-care-demo__reply">
            <span className="pc-care-demo__role">Care Manager</span>
            <p>Klar, ich kümmere mich darum.</p>
          </div>
        )}
        {showAppointment && (
          <div className="pc-care-demo__appointment" role="group" aria-label="Bestätigter Beispieltermin">
            <div><span>MORGEN</span><time dateTime="15:00">15:00</time></div>
            <strong>Physiotherapie</strong>
            <Check size={19} aria-hidden="true" />
          </div>
        )}
      </div>
      <div className="pc-care-demo__controls">
        <button type="button" className="pc-care-demo__trigger" onClick={start}
          aria-disabled={playing} aria-describedby={hintId}
          aria-label={idle ? "Beispiel ansehen – Care-Demo starten" : phase === "complete" ? "Anliegen sprechen – Demo wiederholen" : "Anliegen sprechen – Demo starten"}>
          <span className="pc-care-demo__mic"><Mic size={25} strokeWidth={1.7} aria-hidden="true" /></span>
          <span className="pc-care-demo__copy">
            <span className="pc-care-demo__prompt">{idle ? "Beispiel ansehen" : "Anliegen sprechen"}</span>
            <span id={hintId} className="pc-care-demo__hint">{idle ? "Tippen zum Starten" : "Demo · ohne Aufnahme"}</span>
          </span>
        </button>
      </div>
      <p className="pc-care-demo__sr-only" role="status" aria-live="polite" aria-atomic="true">
        {phase === "complete" ? "Demo abgeschlossen. Physiotherapie morgen um 15:00 bestätigt." : playing ? "Demo läuft." : ""}
      </p>
    </div>
  );
}
